// TODO: TS: Remove any

import type { EventWithId, JsonString } from "types/all";

import { useMemo } from "react";
import { useMutation, useQueries, useQuery, useQueryClient, UseQueryResult } from "react-query";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import { MODERNIZATION_MIN_YEAR } from "../../configs/Modernization";
import { getChildTableDataKey } from "../../configs/TechnicalDataUtils";
import { PORTFOLIO_NAME_IN_API_BODY } from "../../enums/AssetOverview/portfolio";
import { ENERGY_DASHBOARD_NAME_API_BODY } from "../../enums/energyDashboard";
import { getCrossYear } from "../../functions/Modernization";
import { customApiCall, customPaginatedAllPagesFetchCallParallel } from "../../utils/axios";
import { deleteKeysFromObjects, distinctFilter } from "../../utils/dataManipulation";
import { getMonthString, getWeekdayString } from "../../utils/date";
import {
  deleteBuildingProfileFile,
  deleteChildFilesRow,
  deleteParentFilesRow,
  getChildFiles,
  getDashboardNew,
  getParentFiles,
  patchParentFiles,
  postChildFiles,
  postChildRow,
  postParentRow,
  postTranslation,
} from "../functions/all";
import apiUrls from "../urls";

// ==========================
// Hooks Skleton
// ==========================
export const useGeneralHook = <T>(cacheName: any, fetcher: any, rest?: any): UseQueryResult<T, Error> => {
  return useQuery(cacheName, fetcher, {
    enabled: true,
    ...rest,
  });
};

export const useGeneralGetHook = <T>(
  cacheName: any,
  url: any,
  rest?: any,
  apiCaller: any = customApiCall
): UseQueryResult<T, Error> => {
  return useGeneralHook<T>(cacheName, () => apiCaller.get({ url }), {
    ...rest,
  });
};

export const useGeneralGetHookNoCache = (cacheName: any, url: any, rest?: any, apiCaller: any = customApiCall) => {
  const { data, isFetching, isLoading, ...others } = useGeneralHook(cacheName, () => apiCaller.get({ url }), {
    cacheTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
    ...rest,
  });
  return { data, isLoading: isLoading || isFetching, ...others };
};

export const useGeneralPaginatedAllPagesFetchHook = <T>({
  cacheName,
  rest,
  url,
}: {
  cacheName: unknown;
  rest?: Record<string, unknown>;
  url: string;
}): UseQueryResult<T[], Error> => {
  return useGeneralGetHook<T[]>(cacheName, url, rest, customPaginatedAllPagesFetchCallParallel);
};

const useGeneralHeadHook = (cacheName: any, url: any) => {
  return useQuery(cacheName, () => customApiCall.head({ url }));
};

// ==========================
// Common Hooks
// ==========================
export const useGetBuilding = (projectId: any) => {
  const url = apiUrls.assets.get(projectId);
  return useGeneralGetHook<any>(["building2", projectId], url);
};

export const useGetBuildingEPCInfo = (projectId: any) => {
  const url = apiUrls.assets.getEPC(projectId);
  return useGeneralGetHook<any>(["buildingEPCInfo", projectId], url);
};

export const useGetBuildingESGInfo = (projectId: any) => {
  const url = apiUrls.assets.getESGInfo(projectId);
  return useGeneralGetHook(["buildingESGInfo", projectId], url);
};

export const useGetESGReference = () => {
  const url = apiUrls.assets.getESGReference();
  return useGeneralGetHook(["ESGReference"], url);
};

export const useGetBulidingActiveTabs = (projectId: any) => {
  const url = apiUrls.assets.getPages(projectId);
  const { data, isError, isLoading } = useGeneralGetHook(["ActiveTabs", projectId], url);
  const filteredData = (data as any)?.find((building: any) => building?.project_id === Number(projectId));
  return { data: filteredData || {}, isError, isLoading };
};

export const useGetBuildingCertificates = (projectId: any) => {
  const url = apiUrls.certificates.list(projectId);
  const { data, isLoading } = useGeneralGetHook(["certs2", projectId], url);
  const energyCertificates = ((data as any) || []).filter((e: any) => e.type === "EU EPC");
  const nonEnergyCertificates = ((data as any) || []).filter((e: any) => e.type !== "EU EPC");
  return { data: { energyCertificates, nonEnergyCertificates }, isLoading };
};
export const useCheckAllFilesStatus = (projectId: any, pageName: string) => {
  const isTechnical = pageName === "technicalData";
  const url = apiUrls.files.folders.download(projectId, isTechnical);
  return useGeneralHeadHook(["allFilesExistence", projectId, pageName], url);
};

export const useGetBuildingEvents = (projectId: any): UseQueryResult<EventWithId[], Error> => {
  const url = apiUrls.assets.buildingEvent.list(projectId);
  return useGeneralGetHook<EventWithId[]>(["events", projectId], url);
};

export const postBuildingEvent = async ({ data }: any) => {
  const url = apiUrls.assets.buildingEvent.post;
  return await customApiCall.post({ data, url });
};

export const deleteBuildingEvent = async ({ rowId }: any) => {
  const url = apiUrls.assets.buildingEvent.get(rowId);
  return await customApiCall.delete({ url });
};

export const modifyBuildingEvent = async ({ data, rowId }: any) => {
  const url = apiUrls.assets.buildingEvent.get(rowId);
  return await customApiCall.put({ data, url });
};

export const useAddBuildingEvent = () => {
  const queryClient = useQueryClient();
  return useMutation(postBuildingEvent, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["events", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, projectId } = inputs;
      await queryClient.cancelQueries(["events", projectId]);
      const previousData = queryClient.getQueryData(["events", projectId]);
      queryClient.setQueryData(["events", projectId], (oldData: any) => {
        const newData = [...(oldData ?? [])];
        newData.push({ ...data, id: -2 });
        return newData;
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["events", projectId]);
    },
  });
};

export const useDeleteBuildingEvent = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteBuildingEvent, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["events", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { projectId, rowId } = inputs;
      await queryClient.cancelQueries(["events", projectId]);
      const previousData = queryClient.getQueryData(["events", projectId]);
      queryClient.setQueryData(["events", projectId], (oldData: any) => {
        return oldData?.filter((row: any) => row.id !== rowId);
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["events", projectId]);
    },
  });
};

export const useModifyBuildingEvent = () => {
  const queryClient = useQueryClient();
  return useMutation(modifyBuildingEvent, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["events", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["events", projectId]);
      const previousData = queryClient.getQueryData(["events", projectId]);
      queryClient.setQueryData(["events", projectId], (oldData: any) => {
        return oldData?.map((row: any) => {
          if (row.id === rowId) return { ...row, ...data };
          return row;
        });
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["events", projectId]);
    },
  });
};

// ==========================
// Asset Overview
// ==========================
export const useGetAllBuildings = (UserId: any) => {
  const url = apiUrls.assets.list(UserId);
  return useGeneralGetHook<any[]>(["allBuildings", UserId], url);
};

export const useGetPortfolioData = (userId: any) => {
  const url = apiUrls.assets.paginatedPortfolio(userId);
  return useGeneralPaginatedAllPagesFetchHook({ cacheName: ["portfolioData", userId], url });
};

// ==========================
// Portfolio
// ==========================
export const useGetPortfolioDashboardConfigs = (userId: any) => {
  const url = apiUrls.dynamicDashboard.configs.get(userId, PORTFOLIO_NAME_IN_API_BODY);
  return useGeneralGetHook(["portfolioDashboardConfigs", userId, PORTFOLIO_NAME_IN_API_BODY], url);
};

// ==========================
// Building Profile
// ==========================
export const buildingFilesCacheKey = (projectId: any) => ["buildingFiles", projectId];
export const useGetBuildingFiles = (projectId: any) => {
  const url = apiUrls.layerFiles.buildingPassport.list(projectId);
  return useGeneralGetHook(buildingFilesCacheKey(projectId), url);
};

export const useDeleteBuildingProfileFile = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteBuildingProfileFile, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(buildingFilesCacheKey(context.projectId), context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { fileId, projectId } = inputs;
      await queryClient.cancelQueries(buildingFilesCacheKey(projectId));
      const previousData = queryClient.getQueryData(buildingFilesCacheKey(projectId));
      queryClient.setQueryData(buildingFilesCacheKey(projectId), (oldData: any) => {
        return oldData?.filter((row: any) => row.id !== fileId);
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(buildingFilesCacheKey(projectId));
    },
  });
};

export const useGetBuildingKpis = (projectId: any) => {
  const url = apiUrls.kpis.list(projectId);
  return useGeneralGetHook(["kpis", projectId], url);
};
export const useCheckFloorPlanAllFilesStatus = (projectId: any) => {
  const url = apiUrls.layerFiles.buildingPassport.download(projectId);
  return useGeneralHeadHook(["floorPlanAllFiles", projectId], url);
};
export const useGetBulidingUsage = (projectId: any) => {
  const url = apiUrls.buildingUsage.list(projectId);
  return useGeneralGetHook(["bulidingUsage", projectId], url);
};

export const useGetCrremBothQuantities = (projectId: any, scenario: any) => {
  const { data: consumption, isLoading: consumptionIsLoading } = useGetCrrem(projectId, scenario, "consumption");
  const { data: emission, isLoading: emissionIsLoading } = useGetCrrem(projectId, scenario, "emission");

  const isLoading = consumptionIsLoading || emissionIsLoading;
  return { consumption, emission, isLoading };
};

export const useGetKPIs = (projectId: any, scenario: any) => {
  let consumptionKPI: any;
  let emissionKPI: any;
  const { consumption, emission, ...rest } = useGetCrremBothQuantities(projectId, scenario);

  if ((emission as any)?.length) emissionKPI = (emission as any)[0]?.climate;
  if ((consumption as any)?.length) consumptionKPI = (consumption as any)[0]?.climate;

  const strandingYearInterval: any = getCrossYear(emission as any, "climate");
  const strandingYear = strandingYearInterval?.[0]?.length && strandingYearInterval[0][0];

  return {
    ...rest,
    stranding_year: strandingYear ?? "Stranded",
    total_carbon_emission: emissionKPI,
    total_energy_consumption: consumptionKPI,
  };
};

// ==========================
// Tech Data
// ==========================
export const useGetTechData = ({ projectId, tabNames }: any) => {
  const allParentsFiles = useQueries(
    tabNames.map((tab: any) => ({
      queryFn: () => getParentFiles({ activeTab: tab, projectId }),
      queryKey: ["techDataAllParents", tab, projectId],
      select: (data: any) => {
        const newData = data?.map((row: any) => {
          const hasFiles = row?.layers?.some((layer: any) => layer.file_url);
          return { ...row, hasFiles };
        });
        return { data: newData, tab };
      },
    }))
  );
  const isLoading = allParentsFiles?.some((result: any) => result.isLoading);
  const isError = allParentsFiles?.some((result: any) => result.isError);
  return { data: allParentsFiles, isError, isLoading };
};

export const useGetChildrenFiles = ({ activeTab, childIds }: any) => {
  const childrenFiles = useQueries(
    (childIds || []).map((id: any) => ({
      queryFn: () => getChildFiles({ activeTab, layerId: id }),
      queryKey: ["techDataChild", activeTab, id],
      select: (data: any) => ({ ...data, id }),
    }))
  );
  const isLoading = childrenFiles?.some((result: any) => result.isLoading);
  return { data: childrenFiles, isLoading };
};

export const useModifyParentTableCell = () => {
  const queryClient = useQueryClient();
  return useMutation(patchParentFiles, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, data, onSettled, onSuccess, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => {
        return oldData?.map((row: any) => {
          if (row.id === rowId) return { ...row, ...data };
          return row;
        });
      });
      return { activeTab, onSettled, onSuccess, previousData, projectId };
    },
    onSettled: (_inputs: any, _error: any, _variables: any, context: any) => {
      const { activeTab, onSettled, projectId } = context;
      onSettled?.();
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
    onSuccess: (_data: any, _error: any, context: any) => {
      const { onSuccess } = context as any;
      onSuccess?.();
    },
  });
};

export const useDeleteParentTableRow = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteParentFilesRow, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => {
        return oldData?.filter((row: any) => row.id !== rowId);
      });
      return { activeTab, previousData, projectId };
    },
    onSettled: (_error: any, _output: any, context: any) => {
      const { activeTab, projectId } = context as any;
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
  });
};

export const useModifyChildTableCell = () => {
  const queryClient = useQueryClient();
  return useMutation(postChildFiles, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, data, layerId, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => {
        return oldData?.map((parentRow: any) => {
          if (parentRow.id === rowId) {
            const newLayers = parentRow?.layers?.map((childRow: any) => {
              if (childRow.id === layerId) return { ...childRow, ...data };
              return childRow;
            });
            return { ...parentRow, layers: newLayers };
          }
          return parentRow;
        });
      });
      return { activeTab, previousData, projectId };
    },
    onSettled: (_error: any, _output: any, context: any) => {
      const { activeTab, projectId } = context as any;
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
  });
};

export const useAddParentTableRow = () => {
  const queryClient = useQueryClient();
  return useMutation(postParentRow, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, data, projectId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => [
        ...(oldData ?? []),
        { ...data, id: uuidv4() },
      ]);
      return { activeTab, previousData, projectId };
    },
    onSettled: (_error: any, _output: any, context: any) => {
      const { activeTab, projectId } = context as any;
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
  });
};

export const useAddChildTableRow = () => {
  const queryClient = useQueryClient();
  return useMutation(postChildRow, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, data, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => {
        return oldData?.map((parentRow: any) => {
          if (parentRow.id === rowId) {
            const layerDataKey = getChildTableDataKey(activeTab) as string;
            const currentChildren = parentRow?.[layerDataKey];
            const newChild = { id: uuidv4(), ...data };

            const newLayers = currentChildren?.length ? [...currentChildren, newChild] : [newChild];
            return { ...parentRow, [layerDataKey]: newLayers };
          }
          return parentRow;
        });
      });

      return { activeTab, previousData, projectId };
    },
    onSettled: (_error: any, _output: any, context: any) => {
      const { activeTab, projectId } = context as any;
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
  });
};

export const useDeleteChildTableRow = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteChildFilesRow, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["techDataAllParents", context.activeTab, context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { activeTab, layerId, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["techDataAllParents", activeTab, projectId]);
      const previousData = queryClient.getQueryData(["techDataAllParents", activeTab, projectId]);
      queryClient.setQueryData(["techDataAllParents", activeTab, projectId], (oldData: any) => {
        return oldData?.map((parentRow: any) => {
          if (parentRow.id === rowId) {
            const layerDataKey = getChildTableDataKey(activeTab) as string;
            const newLayers = parentRow?.[layerDataKey]?.filter((childRow: any) => childRow.id !== layerId);
            return { ...parentRow, [layerDataKey]: newLayers };
          }
          return parentRow;
        });
      });
      return { activeTab, previousData, projectId };
    },
    onSettled: (_error: any, _output: any, context: any) => {
      const { activeTab, projectId } = context as any;
      queryClient.invalidateQueries(["techDataAllParents", activeTab, projectId]);
    },
  });
};

export const useCheckTechDataDownloadFilesStatus = (projectId: any, activeTab: any) => {
  const url = (apiUrls as any).layerFiles.techData?.[activeTab]?.downloadAll?.(projectId);
  return useGeneralHeadHook(["TechDataFiles", projectId, activeTab], url);
};
// ==========================
// Energy Performance
// ==========================
export const useGetPerformanceIndicator = (projectId: any) => {
  const url = apiUrls.performanceIndicator.list({ projectId });
  return useGeneralGetHook(["performanceIndicator", projectId], url);
};

export const useGetEnergyMix = (projectId: any) => {
  const url = apiUrls.energyMix.list({ projectId });
  return useGeneralGetHook(["energyMix", projectId], url);
};

export const useGetDashboard = (inputs: any) => {
  const { enabled, endDate, interval, projectId, quantity, startDate } = inputs;
  const url = (apiUrls.energyPerformance as any)[interval].list({
    endDate,
    projectId,
    quantity,
    startDate,
  });
  return useGeneralGetHook(["dashboard", projectId, quantity, startDate, endDate, interval], url, { enabled });
};

const addExtraFieldsToDashboardData = (data: any[] | undefined, interval: string | undefined) => {
  if (!data?.length || !interval) return;
  return data.map((row: any) => {
    let dateTimeString = row.datetime;
    if (interval === "monthly") dateTimeString += "-01 00:00:00";
    if (interval === "daily") dateTimeString += " 00:00:00";
    const dateTime = new Date(dateTimeString + " UTC");
    // TODO: Check what were these lines for?
    // const parts = dateTimeString.split(/[- :]/);
    // const dateTime = new Date(
    //   Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5])
    // )

    const year = dateTime.getUTCFullYear();
    const monthNumber = dateTime.getUTCMonth() + 1;
    const month = getMonthString(monthNumber - 1);
    const yearMonth = month + " " + year;
    const dayOfMonth = dateTime.getUTCDate();
    const hourOfDay = dateTime.getUTCHours();

    const dayOfWeekInteger = dateTime.getUTCDay();
    const dayOfWeek = getWeekdayString(dayOfWeekInteger);

    const startOfYear = new Date(Date.UTC(dateTime.getUTCFullYear(), 0, 1, 0, 0, 0));
    const timeDifference = dateTime.getTime() - startOfYear.getTime();
    const dayOfYear = Math.floor(timeDifference / (1000 * 60 * 60 * 24)) + 1;
    const hourOfYear = Math.floor(timeDifference / (1000 * 60 * 60)) + 1;
    const cetHour = (hourOfDay + 1) % 24;
    const isWorkingDay = dayOfWeekInteger >= 1 && dayOfWeekInteger <= 5 ? 1 : 0;
    const isWorkingHour = isWorkingDay && hourOfDay <= 18 && hourOfDay >= 7 ? 1 : 0;

    return {
      ...row,
      cetHour,
      dayOfMonth,
      dayOfWeek,
      dayOfYear,
      hourOfDay,
      hourOfYear,
      isWorkingDay,
      isWorkingHour,
      month,
      monthNumber,
      year,
      yearMonth,
    };
  });
};

export const useGetDashboardNew = (inputs: any) => {
  const { dataLevel, enabled, endDate, interval, projectId, quantities, scenarios, startDate } = inputs;
  const allSegments: { quantity: any; scenario: any }[] = [];
  scenarios?.forEach((scenario: any) => {
    quantities?.forEach((quantity: any) => {
      allSegments.push({ quantity, scenario });
    });
  });
  const dashboardNew = useQueries(
    allSegments.map(({ quantity, scenario }) => {
      const apiCallInputs = {
        dataLevel,
        endDate,
        interval,
        projectId,
        quantity,
        scenario,
        startDate,
      };
      return {
        enabled,
        queryFn: () => getDashboardNew(apiCallInputs),
        queryKey: ["dashboardNew", scenario, dataLevel, quantity, projectId, startDate, endDate, interval],
        select: (data: any) => {
          const { columns, reformattedValues } = data ?? {};
          const newData = reformattedValues?.map((row: any) => ({ ...row, dataPoint: quantity, scenario }));
          return { columns, data: newData };
        },
      };
    })
  );
  const isLoading = dashboardNew?.some((result: any) => result.isLoading);
  const isError = dashboardNew?.every((result: any) => result.isError);
  let mergedData: any[] = dashboardNew.reduce((total: any[], quantityScenario: any) => {
    return quantityScenario?.status === "success" && quantityScenario?.data?.data?.length
      ? [...total, ...(quantityScenario?.data?.data ?? [])]
      : total;
  }, [] as any[]);

  let apiColumns: any[] = dashboardNew.reduce((total: any[], quantityScenario: any) => {
    return quantityScenario?.status === "success" && quantityScenario?.data?.columns?.length
      ? [...total, ...(quantityScenario?.data?.columns ?? [])]
      : total;
  }, [] as any[]);

  const FILTER_DATA_TO_NONE_ZERO_UTILITIES = true;
  if (FILTER_DATA_TO_NONE_ZERO_UTILITIES) {
    const nullUtilities = apiColumns?.filter((col) =>
      mergedData?.every((row: any) => [0, null, undefined].includes(row?.[col]))
    );

    if (nullUtilities?.length) {
      mergedData = (deleteKeysFromObjects(mergedData, nullUtilities) as any[]) ?? [];
      apiColumns = apiColumns?.filter((col) => !nullUtilities?.includes(col));
    }
  }

  const enrichedData = useMemo(
    () => addExtraFieldsToDashboardData(mergedData, interval),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(inputs) + `${mergedData?.length}`]
  );

  const enrichedDataColumns = Object.keys(enrichedData?.[0] ?? {});

  return {
    allColumns: [...(apiColumns ?? []), ...(enrichedDataColumns ?? [])]?.filter(distinctFilter),
    data: enrichedData,
    isError,
    isLoading,
  };
};

export const useGetEnergyDashbordConfigs = (projectId: any) => {
  const url = apiUrls.dynamicDashboard.configs.get(projectId, ENERGY_DASHBOARD_NAME_API_BODY);
  return useGeneralGetHook(["energyDashbordConfigs", projectId, ENERGY_DASHBOARD_NAME_API_BODY], url);
};

type Metric = {
  dataKey: "consumption";
  level: "building";
};
type ProjectInfo = {
  crrem_scenario_list: JsonString<string[]>;
  id: number;
  metrics: JsonString<Metric[]>;
  project_id: number;
  room_level_scenarios: JsonString<string[]>;
};

export const useGetProjectInfo = (
  projectId: any,
  initialData?: ProjectInfo | Record<string, unknown> | undefined
): UseQueryResult<ProjectInfo[], Error> => {
  const url = apiUrls.energyPerformanceNew.projectInfo.list(projectId);
  return useGeneralGetHook(["projectInfo", projectId], url, [{ initialData }]);
};

// ==========================
// Certificates
// ==========================
export const useGetCertificateStatus = (projectId: any) => {
  const url = apiUrls.certificates.certificationStatus.list({ projectId });
  return useGeneralGetHook(["certificateStatus", projectId], url);
};

export const useGetEnergyRatingStatus = (projectId: any) => {
  const url = apiUrls.certificates.energyRatingStatus.list({ projectId });
  return useGeneralGetHook(["energyRatingStatus", projectId], url);
};
export const useCheckCertificatesDownloadFilesStatus = (projectId: any, certificateType: any) => {
  const url = apiUrls.files.certificates.download(projectId, certificateType);
  return useGeneralHeadHook(["certificatesFiles", projectId, certificateType], url);
};
// ==========================
// Modernization
// ==========================
export const useGetGoals = (projectId: any) => {
  const url = apiUrls.modernization.goals.list(projectId);
  return useGeneralGetHook(["getGoals", projectId], url);
};

export const useGetScenarios = (projectId: any) => {
  const url = apiUrls.modernization.scenarios.list(projectId);
  return useGeneralGetHook(["getScenarios", projectId], url);
};
export const useCheckScenariosFilesStatus = (projectId: any) => {
  const url = apiUrls.modernization.scenarioFiles.download(projectId);
  return useGeneralHeadHook(["scenariosFiles", projectId], url);
};
// ==========================
// Translations
// ==========================
export const useGetTranslations = () => {
  const url = apiUrls.translation.get("de");
  return useGeneralGetHook("getTranslations", url);
};

export const useModifyTranslation = () => {
  const queryClient = useQueryClient();
  return useMutation(postTranslation, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData("getTranslations", context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data } = inputs;
      await queryClient.cancelQueries("getTranslations");
      const previousData = queryClient.getQueryData("getTranslations");
      queryClient.setQueryData("getTranslations", (oldData: any) => ({ ...(oldData ?? {}), ...data }));
      return { previousData };
    },
    onSettled: () => {
      queryClient.invalidateQueries("getTranslations");
    },
    onSuccess: () => {
      toast.success("Successfully modified.");
    },
  });
};

// ============== CRREM TOOL
export const useGetCrrem = (projectId: any, degree: any, parameter: any, enabled = true) => {
  const url = apiUrls.modernization.crrem.list({ degree, parameter, projectId });
  const { data, ...rest } = useGeneralGetHook(["crrem", projectId, degree, parameter], url, {
    enabled: enabled && Boolean(projectId && degree),
  });
  return useMemo(
    () => ({ data: (data as any)?.filter((row: any) => row.year >= MODERNIZATION_MIN_YEAR), ...rest }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );
};

//=============================
// Wizard
//============================
export const useGetAllMeasures = (projectId: any, enabled?: boolean) => {
  const url = apiUrls.modernization.retrofits.list(projectId);
  return useGeneralGetHook<any[]>(["allMeasures", projectId], url, { enabled });
};

export const postMeasure = async ({ data }: any) => {
  const url = apiUrls.modernization.retrofits.post;
  return await customApiCall.post({ data, url });
};

export const deleteMeasure = async ({ rowId }: any) => {
  const url = apiUrls.modernization.retrofits.get(rowId);
  return await customApiCall.delete({ url });
};

export const modifyMeasure = async ({ data, rowId }: any) => {
  const url = apiUrls.modernization.retrofits.get(rowId);
  return await customApiCall.patch({ data, url });
};

export const useAddMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(postMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["allMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, onSettled: onReqSettled, onSuccess: onReqSuccess, projectId } = inputs;
      await queryClient.cancelQueries(["allMeasures", projectId]);
      const previousData = queryClient.getQueryData(["allMeasures", projectId]);
      queryClient.setQueryData(["allMeasures", projectId], (oldData: any) => {
        const newData = [...(oldData ?? [])];
        newData.push({ ...data, id: -2 });
        return newData;
      });
      return { onReqSettled, onReqSuccess, previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { onReqSettled, projectId } = context;
      queryClient.invalidateQueries(["allMeasures", projectId]);
      onReqSettled?.();
    },
    onSuccess: (_data: any, _error: any, context: any) => {
      const { onReqSuccess } = context as any;
      onReqSuccess?.();
    },
  });
};

export const useDeleteMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["allMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { projectId, rowId } = inputs;
      await queryClient.cancelQueries(["allMeasures", projectId]);
      const previousData = queryClient.getQueryData(["allMeasures", projectId]);
      queryClient.setQueryData(["allMeasures", projectId], (oldData: any) => {
        return oldData?.filter((row: any) => row.id !== rowId);
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["allMeasures", projectId]);
      queryClient.invalidateQueries(["selectedMeasures", projectId]);
    },
  });
};

export const useModifyMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(modifyMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["allMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, onSettled: onReqSettled, onSuccess: onReqSuccess, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["allMeasures", projectId]);
      const previousData = queryClient.getQueryData(["allMeasures", projectId]);
      queryClient.setQueryData(["allMeasures", projectId], (oldData: any) => {
        return oldData?.map((row: any) => {
          if (row.id === rowId) return { ...row, ...data };
          return row;
        });
      });
      return { onReqSettled, onReqSuccess, previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { onReqSettled, projectId } = context;
      queryClient.invalidateQueries(["allMeasures", projectId]);
      queryClient.invalidateQueries(["selectedMeasures", projectId]);
      onReqSettled?.();
    },
    onSuccess: (_data: any, _error: any, context: any) => {
      const { onReqSuccess } = context as any;
      onReqSuccess?.();
    },
  });
};

// ========= User Selected Measures
export const useGetAllSelectedMeasures = (projectId: any, enabled = true) => {
  const url = apiUrls.modernization.selectedRetrofit.list(projectId);
  return useGeneralGetHook<any[]>(["selectedMeasures", projectId], url, { enabled });
};

export const postSelectedMeasure = async ({ data }: any) => {
  const url = apiUrls.modernization.selectedRetrofit.post;
  return await customApiCall.post({ data, url });
};

export const deleteSelectedMeasure = async ({ rowId }: any) => {
  const url = apiUrls.modernization.selectedRetrofit.get(rowId);
  return await customApiCall.delete({ url });
};

export const modifySelectedMeasure = async ({ data, rowId }: any) => {
  const url = apiUrls.modernization.selectedRetrofit.get(rowId);
  return await customApiCall.patch({ data, url });
};

export const useAddSelectedMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(postSelectedMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["selectedMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, projectId } = inputs;
      await queryClient.cancelQueries(["selectedMeasures", projectId]);
      const previousData = queryClient.getQueryData(["selectedMeasures", projectId]);
      queryClient.setQueryData(["selectedMeasures", projectId], (oldData: any) => {
        const newData = [...(oldData ?? [])];
        newData.push({ ...data, id: -2 });
        return newData;
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["selectedMeasures", projectId]);
    },
  });
};

export const useDeleteSelectedMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteSelectedMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["selectedMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { projectId, rowId } = inputs;
      await queryClient.cancelQueries(["selectedMeasures", projectId]);
      const previousData = queryClient.getQueryData(["selectedMeasures", projectId]);
      queryClient.setQueryData(["selectedMeasures", projectId], (oldData: any) => {
        return oldData?.filter((row: any) => row.id !== rowId);
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["selectedMeasures", projectId]);
    },
  });
};

export const useModifySelectedMeasure = () => {
  const queryClient = useQueryClient();
  return useMutation(modifySelectedMeasure, {
    onError: (error: any, _output: any, context: any) => {
      toast.error(JSON.stringify(error?.response?.data));
      queryClient.setQueryData(["selectedMeasures", context.projectId], context.previousData);
    },
    onMutate: async (inputs: any) => {
      const { data, projectId, rowId } = inputs;
      await queryClient.cancelQueries(["selectedMeasures", projectId]);
      const previousData = queryClient.getQueryData(["selectedMeasures", projectId]);
      queryClient.setQueryData(["selectedMeasures", projectId], (oldData: any) => {
        return oldData?.map((row: any) => {
          if (row.id === rowId) return { ...row, ...data };
          return row;
        });
      });
      return { previousData, projectId };
    },
    onSettled: (_data: any, _error: any, _variables: any, context: any) => {
      const { projectId } = context;
      queryClient.invalidateQueries(["selectedMeasures", projectId]);
    },
  });
};
